import { PropsWithChildren, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import * as Yup from "yup";
import { useFormik } from "formik";

import {
  Select,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectGroup,
  SelectItem,
  Button,
  Label,
  DatePicker,
} from "@/components/ui";
import { useToast } from "@/hooks";
import { DisplayError, formatDateToBackend } from "@/utils";

import { SheetFilterContainer } from "../../sheets";

type FormFilterLeadsProps = PropsWithChildren;

export function FormFilterLeads({ children }: FormFilterLeadsProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const initialValues = {
    source: queryParams.get("source") || "all",
    status: queryParams.get("status") || "all",
    converted_to_patient: queryParams.get("converted_to_patient") || "all",
    start_date: queryParams.get("start_date") || "",
    end_date: queryParams.get("end_date") || "",
  };

  const {
    handleSubmit,
    getFieldProps,
    isSubmitting,
    resetForm,
    ...formik
  } = useFormik({
    initialValues,
    validationSchema: Yup.object().shape({
      source: Yup.string().optional(),
      status: Yup.string().optional(),
      converted_to_patient: Yup.string().optional(),
      start_date: Yup.string().optional(),
      end_date: Yup.string().optional(),
    }),

    onSubmit: async (data) => {
      try {
        const params = new URLSearchParams(location.search);

        if (data.source && data.source !== "all") {
          params.set("source", data.source);
        } else {
          params.delete("source");
        }

        if (data.status && data.status !== "all") {
          params.set("status", data.status);
        } else {
          params.delete("status");
        }

        if (data.converted_to_patient && data.converted_to_patient !== "all") {
          params.set("converted_to_patient", data.converted_to_patient);
        } else {
          params.delete("converted_to_patient");
        }

        if (data.start_date) {
          params.set("start_date", formatDateToBackend(data.start_date));
        } else {
          params.delete("start_date");
        }

        if (data.end_date) {
          params.set("end_date", formatDateToBackend(data.end_date));
        } else {
          params.delete("end_date");
        }

        params.delete("page");

        navigate(`?${params.toString()}`, { replace: true });
      } catch (error: any) {
        toast({
          title: t("LABEL.error"),
          description: DisplayError(error),
          variant: "destructive",
        });
      }
    },
  });

  const handleClearFilters = () => {
    resetForm();
    const params = new URLSearchParams(location.search);
    params.delete("source");
    params.delete("status");
    params.delete("converted_to_patient");
    params.delete("start_date");
    params.delete("end_date");
    params.delete("page");
    navigate(`?${params.toString()}`, { replace: true });
  };

  const sourceOptions = [
    { value: "all", label: "Todas" },
    { value: "site", label: "Site" },
    { value: "indicacao", label: "Indicação" },
    { value: "rede_social", label: "Rede Social" },
    { value: "email", label: "E-mail Marketing" },
    { value: "telefone", label: "Telefone" },
    { value: "evento", label: "Evento" },
    { value: "WhatsApp", label: "WhatsApp" },
    { value: "outro", label: "Outro" },
  ];

  useEffect(() => {
    const hasFilters =
      queryParams.get("source") ||
      queryParams.get("status") ||
      queryParams.get("converted_to_patient") ||
      queryParams.get("start_date") ||
      queryParams.get("end_date");

    if (!hasFilters) {
      resetForm();
    }
  }, [queryParams, resetForm]);

  return (
    <SheetFilterContainer button={children}>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Origem</Label>
            <Select
              value={formik.values.source}
              onValueChange={(value) => formik.setFieldValue("source", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione a origem" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {sourceOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Status</Label>
            <Select
              value={formik.values.status}
              onValueChange={(value) => formik.setFieldValue("status", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione o status" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="new">Novo</SelectItem>
                  <SelectItem value="contacted">Contatado</SelectItem>
                  <SelectItem value="qualified">Qualificado</SelectItem>
                  <SelectItem value="proposal">Proposta</SelectItem>
                  <SelectItem value="won">Ganho</SelectItem>
                  <SelectItem value="lost">Perdido</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Convertido</Label>
            <Select
              value={formik.values.converted_to_patient}
              onValueChange={(value) =>
                formik.setFieldValue("converted_to_patient", value)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="true">Sim</SelectItem>
                  <SelectItem value="false">Não</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Data Início</Label>
            <DatePicker
              {...getFieldProps("start_date")}
            />
          </div>

          <div className="space-y-2">
            <Label>Data Fim</Label>
            <DatePicker
              {...getFieldProps("end_date")}
            />
          </div>
        </div>

        <div className="flex gap-2 pt-4">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={handleClearFilters}
          >
            Limpar
          </Button>
          <Button type="submit" className="flex-1" disabled={isSubmitting}>
            Aplicar
          </Button>
        </div>
      </form>
    </SheetFilterContainer>
  );
}
